import React from "react";
import { DataTable } from "@/components/ui/data-table";
import { Button } from "@/components/ui/button";
import { Plus, Loader2 } from "lucide-react";
import AddWellnessDialog from "./AddWellnessDialog";
import { axiosPrivate } from "@/api/axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ColumnDef } from "@tanstack/react-table";
import { formatDateTimeGMT5 } from "@/utils/timezone";
import ConfirmDialog from "@/components/dashboard/ConfirmDialog";
import ActionsMenu from "@/components/dashboard/ActionsMenu";
import { toast } from "sonner";

type WellnessActivity = {
  id: number;
  title: string;
  type: string;
  steps: string[];
  duration: string;
  benefits: string;
  created_at?: string;
};

function EmotionalWellnessFactsTable() {
  const [open, setOpen] = React.useState(false);
  const [editingActivity, setEditingActivity] = React.useState<WellnessActivity | null>(null);
  const [openConfirmDialog, setOpenConfirmDialog] = React.useState(false);
  const confirmActionButtonRef = React.useRef<() => void>(() => {});
  const queryClient = useQueryClient();

  const { data: rawWellness, isLoading } = useQuery({
    queryKey: ["wellness", "therapy"],
    queryFn: async () => {
      const res = await axiosPrivate.get("/wellness/type/therapy");
      const payload = Array.isArray(res.data?.data) ? res.data.data : res.data;
      return Array.isArray(payload) ? payload : [];
    },
    placeholderData: [],
  });

  const wellnessActivities: WellnessActivity[] = React.useMemo(() => {
    const list = rawWellness || [];
    return list.map((w: any) => ({
      id: Number(w.id ?? 0),
      title: String(w.title ?? ""),
      type: String(w.type ?? "therapy"),
      steps: Array.isArray(w.steps) ? w.steps : [],
      duration: String(w.duration ?? ""),
      benefits: String(w.benefits ?? ""),
      created_at: w.created_at ? String(w.created_at) : undefined,
    }));
  }, [rawWellness]);

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await axiosPrivate.delete(`/wellness/${id}/`);
      return res.data;
    },
    onMutate: () => toast.loading("Deleting wellness activity..."),
    onSuccess: () => {
      toast.dismiss();
      toast.success("Wellness activity deleted");
      queryClient.invalidateQueries({ queryKey: ["wellness", "therapy"] });
      setOpenConfirmDialog(false);
    },
    onError: () => {
      toast.dismiss();
      toast.error("Failed to delete wellness activity");
      setOpenConfirmDialog(false);
    },
  });

  const columns: ColumnDef<WellnessActivity>[] = React.useMemo(() => [
    { accessorKey: "id", header: "ID" },
    { accessorKey: "title", header: "Title" },
    { accessorKey: "type", header: "Type" },
    {
      accessorKey: "steps",
      header: "Steps",
      cell: ({ row }) => {
        const steps = row.getValue("steps") as string[];
        return <span className="text-sm">{steps.length} step{steps.length !== 1 ? 's' : ''}</span>;
      },
    },
    { accessorKey: "duration", header: "Duration" },
    {
      accessorKey: "benefits",
      header: "Benefits",
      cell: ({ row }) => {
        const benefits = row.getValue("benefits") as string;
        return <span className="truncate max-w-[200px] inline-block" title={benefits}>{benefits}</span>;
      },
    },
    {
      accessorKey: "created_at",
      header: "Created At",
      cell: ({ row }) => {
        const ts = row.getValue("created_at") as string | undefined;
        if (!ts) return <span className="text-muted-foreground">N/A</span>;
        const d = new Date(ts);
        if (isNaN(d.getTime())) return <span className="text-muted-foreground">Invalid</span>;
        return <span>{formatDateTimeGMT5(d)}</span>;
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const activity = row.original;
        return (
          <ActionsMenu
            onEdit={() => {
              setEditingActivity(activity);
              setOpen(true);
            }}
            onDelete={() => {
              confirmActionButtonRef.current = () => {
                setOpenConfirmDialog(false);
                deleteMutation.mutate(Number(activity.id));
              };
              setOpenConfirmDialog(true);
            }}
          />
        );
      },
    },
  ], [deleteMutation.mutate]);

  return (
    <div className="space-y-4">
      <h1 className="text-3xl">Daily Emotional Therapy</h1>
      <p>Manage all the emotional therapy activities in one place.</p>
      <div className="flex justify-start">
        <Button size="sm" onClick={() => {
          setEditingActivity(null);
          setOpen(true);
        }} className="flex items-center gap-2">
          <Plus size={16} /> Add Therapy
        </Button>
      </div>
      {isLoading ? (
        <div className="flex justify-center items-center h-32">
          <Loader2 className="animate-spin w-6 h-6" />
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={wellnessActivities}
          placeholder="Filter Wellness Activities"
          filterColumns={["title"]}
        />
      )}
      <AddWellnessDialog
        open={open}
        onOpenChange={(open) => {
          setOpen(open);
          if (!open) setEditingActivity(null);
        }}
        defaultType="therapy"
        lockType={true}
        editMode={!!editingActivity}
        editData={editingActivity || undefined}
        onCreated={() => {
          queryClient.invalidateQueries({ queryKey: ["wellness", "therapy"] });
        }}
        onUpdated={() => {
          queryClient.invalidateQueries({ queryKey: ["wellness", "therapy"] });
        }}
      />
      <ConfirmDialog
        open={openConfirmDialog}
        onOpenChange={setOpenConfirmDialog}
        actionButtonRef={confirmActionButtonRef}
        isLoading={deleteMutation.isPending}
        AlertdialogDescription="Are you sure you want to delete this wellness activity? This action cannot be undone."
      />
    </div>
  );
}

export default EmotionalWellnessFactsTable;
